import { css } from '@compiled/react';
import sanitizeHtml from 'sanitize-html';

import { media } from '@atlaskit/primitives';

import { PullQuote } from '@/components/wpl/pull-quote/src/pull-quote';

import Checklist from '../../../components/checklist/src/checklist';
import { Image } from '../../../components/wpl/image/src/image';
import { Row } from '../../../libraries/wpl/foundations/src/grid';

// ================================================
// Types
// ================================================

type ChecklistWithImageProps = {
	title: string;
	listItems?: (string | JSX.Element)[];
	rawHtmlContent?: string;
	imageSrc: string;
	imageAlt: string;
	isIncludeTestimony?: boolean;
	quoteBlurb?: string;
	quoteName?: string;
	quoteCompany?: string;
	testimonyImageSrc?: string;
	testimonyImageAlt?: string;
	isShowTestimonyBorder?: boolean;
	testimonyIsUsingTextQuotationMarks?: boolean;
	isSmallQuote?: boolean;
	isIncludeHorizontalMobileRule?: boolean;
};

// ================================================
// CSS
// ================================================
const checklistWithNoPullQuoteStyle = css({
	[media.below.sm]: {
		paddingBlockEnd: '32px',
	},
});

const titleStyles = css({
	marginBlockStart: 0,
	fontWeight: 500,
	color: '#253858',
	letterSpacing: 0,
	'@media (min-width: 768px)': {
		fontSize: '2.25rem',
	},
});

// ================================================
// Rendering Functions
// ================================================

const HorizontalRuleMobile: React.FC = () => (
	<Row layout="12">
		<div
			css={css({
				display: 'none',
				[media.below.sm]: {
					// eslint-disable-next-line local/ensure-wac-token-usage
					borderBlockEnd: '2px solid #D9D9D9',
					display: 'block',
				},
			})}
		/>
	</Row>
);
const ChecklistWithImage = ({
	title,
	listItems,
	rawHtmlContent,
	imageSrc,
	imageAlt,
	isIncludeTestimony = false,
	quoteBlurb,
	quoteName,
	quoteCompany,
	testimonyImageSrc,
	testimonyImageAlt,
	isShowTestimonyBorder = true,
	testimonyIsUsingTextQuotationMarks = false,
	isSmallQuote = false,
	isIncludeHorizontalMobileRule = true,
}: // eslint-disable-next-line complexity
ChecklistWithImageProps) => {
	return (
		<section css={!isIncludeTestimony && checklistWithNoPullQuoteStyle}>
			<Row layout="4-8">
				<div>
					<h3 css={titleStyles}>{title}</h3>
					{listItems && listItems.length && <Checklist listItems={listItems} />}
					{/* Allow for raw HTML content to be passed in from Content API */}
					{rawHtmlContent && (
						<div
							className="tab-content"
							dangerouslySetInnerHTML={{
								__html: sanitizeHtml(rawHtmlContent),
							}}
						/>
					)}
				</div>
				<div>
					<Image src={imageSrc} alt={imageAlt} />
				</div>
			</Row>
			{isIncludeTestimony && isIncludeHorizontalMobileRule && <HorizontalRuleMobile />}
			{isIncludeTestimony && (
				<div
					css={[
						isSmallQuote ? css({ paddingBlockStart: '60px' }) : css({ paddingBlockStart: '16px' }),
					]}
				>
					<PullQuote
						logoSrc={testimonyImageSrc}
						logoAltText={testimonyImageAlt}
						quoteBlurb={quoteBlurb || ''}
						quoteCompany={quoteCompany || ''}
						quoteName={quoteName || ''}
						isShowBorder={isShowTestimonyBorder}
						isUsingTextQuotationMarks={testimonyIsUsingTextQuotationMarks}
						isSmall={isSmallQuote}
					/>
				</div>
			)}
		</section>
	);
};

export default ChecklistWithImage;
